/** Utilidades de la contabilidad por partida doble (núcleo `@jr2/contable`, régimen PGC). */
import { useEffect, useState } from 'react'
import type { IssuerDto } from '@toga/shared'
import { useAuth } from '../contexts/AuthContext'
import { useIssuersState } from '../components/accounting/common'

/** Emisor que lleva su contabilidad por partida doble en Toga. */
export const issuerHasPgc = (i: IssuerDto): boolean => i.active && i.accountingRegime === 'PGC'

/**
 * Emisor con libros del autónomo: gastos, inversiones y borradores 303/130. Es una persona física
 * con libros ligeros o con PGC (una sociedad en PGC no usa el 130).
 */
export const issuerHasBooks = (i: IssuerDto): boolean =>
  i.active &&
  i.naturalPerson &&
  (i.accountingRegime === 'IRPF_BOOKS' || i.accountingRegime === 'PGC')

/** ADMIN y ABOGADO ven los libros; el resto no. */
export function useCanSeeLedger(): boolean {
  const auth = useAuth()
  return (
    auth.status === 'authenticated' && (auth.user.role === 'ADMIN' || auth.user.role === 'ABOGADO')
  )
}

/** Los emisores en PGC y el elegido (el primero por defecto). */
export function usePgcIssuer() {
  const { issuers, loaded } = useIssuersState()
  const pgc = issuers.filter(issuerHasPgc)
  const [chosen, setChosen] = useState('')
  const issuer = pgc.find((i) => i.id === chosen) ?? pgc[0] ?? null
  useEffect(() => {
    if (!chosen && pgc[0]) setChosen(pgc[0].id)
  }, [chosen, pgc])
  return { pgc, issuer, issuerId: issuer?.id ?? '', setIssuerId: setChosen, loaded }
}

export const todayYmd = (): string => new Date().toISOString().slice(0, 10)

/** De dónde viene un asiento que Toga genera solo (null = asiento manual). */
export function entryOrigin(
  source: { type: string; id: string; purpose: string } | null
): { label: string; href: string | null } | null {
  if (!source) return null
  const reversal = source.purpose.endsWith(':REVERSAL')
  switch (source.type) {
    case 'SALES_INVOICE':
      return { label: reversal ? 'Anulación de factura' : 'Factura', href: `/billing/${source.id}` }
    case 'SALES_INVOICE_PAYMENT':
      return { label: reversal ? 'Cobro anulado' : 'Cobro de factura', href: null }
    case 'FUNDS_PROVISION':
      return {
        label: source.purpose.startsWith('REFUND')
          ? 'Devolución de provisión'
          : reversal
            ? 'Provisión eliminada'
            : 'Provisión de fondos',
        href: null,
      }
    default:
      return { label: source.type, href: null }
  }
}
